/* Veyro: native-search launcher and scroll-snap collection controls. No cart writes. */
(() => {
  class VeyroSearchLauncher extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const input = this.querySelector('input[name="q"]');
      const form = this.querySelector('form');
      if (!input || !form) return;
      const { signal } = this.controller;
      const open = () => {
        const modal = document.getElementById('search-modal');
        if (!this.isConnected || typeof modal?.showDialog !== 'function') return false;
        const target = modal.querySelector('input[name="q"]');
        if (!target) return false;
        try {
          modal.showDialog();
          cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(() => {
            this.frame = undefined;
            if (!this.isConnected || signal.aborted || !target.isConnected || !modal.querySelector('dialog')?.open) return;
            if (input.value.trim()) {
              target.value = input.value;
              target.dispatchEvent(new Event('input', { bubbles: true }));
            }
            target.focus({ preventScroll: true });
          });
          return true;
        } catch { return false; }
      };
      // Do not reopen on focus restored by the dialog's native close operation.
      input.addEventListener('click', open, { signal });
      input.addEventListener('input', open, { signal });
      input.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown' && open()) event.preventDefault();
      }, { signal });
      form.addEventListener('submit', event => {
        if (!input.value.trim()) {
          event.preventDefault();
          if (!open()) input.focus();
        }
        // Nonempty Enter/submit keeps the native full-results fallback.
      }, { signal });
    }
    disconnectedCallback() {
      this.controller?.abort();
      cancelAnimationFrame(this.frame);
      this.frame = undefined;
    }
  }
  class VeyroCategoryCarousel extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = new AbortController();
      this.track = this.querySelector('[data-category-track]');
      if (!this.track) return;
      const { signal } = this.controller;
      this.previous = this.querySelector('[data-category-previous]');
      this.next = this.querySelector('[data-category-next]');
      const update = () => {
        if (!this.isConnected) return;
        const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const left = Math.max(0, this.track.scrollLeft);
        if (this.previous) this.previous.disabled = max < 2 || left < 2;
        if (this.next) this.next.disabled = max < 2 || left >= max - 2;
      };
      const move = direction => {
        const card = this.track.querySelector('a');
        const distance = card ? card.getBoundingClientRect().width + 16 : this.track.clientWidth * .86;
        this.track.scrollBy({ left: direction * distance, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      };
      this.previous?.addEventListener('click', () => move(-1), { signal });
      this.next?.addEventListener('click', () => move(1), { signal });
      this.track.addEventListener('scroll', update, { passive: true, signal });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      if (typeof ResizeObserver === 'function') {
        this.observer = new ResizeObserver(update);
        this.observer.observe(this.track);
      }
      update();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }
  }
  if (!customElements.get('veyro-search-launcher')) customElements.define('veyro-search-launcher', VeyroSearchLauncher);
  if (!customElements.get('veyro-category-carousel')) customElements.define('veyro-category-carousel', VeyroCategoryCarousel);
})();

/* Native product sharing and controls for the existing Horizon zoom dialog. */
(() => {
  class VeyroProductShare extends HTMLElement {
    connectedCallback() {
      this.controller?.abort(); this.controller = new AbortController();
      const {signal} = this.controller;
      const button=this.querySelector('[data-share-button]'), panel=this.querySelector('[data-share-panel]'), copy=this.querySelector('[data-share-copy]'), native=this.querySelector('[data-share-native]'), telegram=this.querySelector('[data-share-telegram]'), fallback=this.querySelector('[data-share-fallback]'), status=this.querySelector('[data-share-status]');
      if(!button||!panel||!copy||!native||!telegram||!fallback||!status)return;
      VeyroProductShare.uid = (VeyroProductShare.uid || 0) + 1;
      panel.id = `VeyroSharePanel-${VeyroProductShare.uid}`;
      button.setAttribute('aria-controls', panel.id);
      fallback.id = `VeyroShareLink-${VeyroProductShare.uid}`;
      const label = this.querySelector('label'); if (label) label.htmlFor = fallback.id;
      const current=()=>this.isConnected&&!signal.aborted;
      let url;
      try { url=new URL(this.dataset.url,location.origin); if(url.protocol!=='https:'||url.origin!==location.origin||!/\/products\/[^/]+\/?$/.test(url.pathname))throw Error('Invalid URL'); url.search='';url.hash=''; }
      catch { button.disabled=true;status.textContent='Посилання на товар недоступне.';return; }
      const close=(focus=false)=>{panel.hidden=true;button.setAttribute('aria-expanded','false');if(focus)button.focus();};
      const open=()=>{panel.hidden=false;button.setAttribute('aria-expanded','true');copy.focus();};
      const share=async()=>{
        if(this.busy)return;this.busy=true;
        try{await navigator.share({title:this.dataset.title||'Veyro',url:url.href});}
        catch(error){if(current()&&error?.name!=='AbortError')open();}
        finally{this.busy=false;}
      };
      telegram.href='https://t.me/share/url?'+new URLSearchParams({url:url.href,text:this.dataset.title||'Veyro'});
      native.hidden=typeof navigator.share!=='function';
      button.addEventListener('click',()=>{
        if(window.matchMedia('(max-width:749px)').matches&&typeof navigator.share==='function'){share();return;}
        if(panel.hidden)open();else close();
      },{signal});
      native.addEventListener('click',()=>{close();share();},{signal});
      copy.addEventListener('click',async()=>{
        if(this.busy)return;this.busy=true;copy.disabled=true;status.textContent='';fallback.hidden=true;
        try{
          if(typeof navigator.clipboard?.writeText!=='function')throw Error('Clipboard unavailable');
          await navigator.clipboard.writeText(url.href);
          if(current())status.textContent='Посилання скопійовано.';
        }catch{
          if(current()){fallback.value=url.href;fallback.hidden=false;fallback.focus();fallback.select();status.textContent='Скопіюй посилання з поля.';}
        }finally{this.busy=false;if(current())copy.disabled=false;}
      },{signal});
      this.addEventListener('keydown',event=>{if(event.key==='Escape'&&!panel.hidden){event.preventDefault();event.stopPropagation();close(true);}},{signal});
      document.addEventListener('click',event=>{if(!this.contains(event.target))close();},{signal});
      this.addEventListener('focusout',event=>{if(event.relatedTarget&&!this.contains(event.relatedTarget))close();},{signal});
    }
    disconnectedCallback(){this.controller?.abort();}
  }
  class VeyroZoomControls extends HTMLElement {
    connectedCallback() {
      this.controller?.abort(); this.observer?.disconnect();
      this.controller = new AbortController();
      const { signal } = this.controller;
      const owner = this.closest('zoom-dialog');
      const previous = this.querySelector('[data-zoom-previous]');
      const next = this.querySelector('[data-zoom-next]');
      const counter = this.querySelector('[data-zoom-counter]');
      if (!owner || !previous || !next || !counter) return;
      const current = () => this.isConnected && !signal.aborted && owner.isConnected;
      const items = () => Array.from(owner.querySelectorAll('[ref="media[]"]'));
      const activeIndex = () => {
        const buttons = Array.from(owner.querySelectorAll('.dialog-thumbnails-list__thumbnail'));
        return Math.max(0, buttons.findIndex(item => item.getAttribute('aria-selected') === 'true'));
      };
      const update = () => {
        if (!current()) return;
        const count = items().length, index = Math.min(activeIndex(), Math.max(0, count - 1));
        const text = count ? `${index + 1} / ${count}` : '';
        if (counter.textContent !== text) counter.textContent = text;
        const ready = typeof owner.handleThumbnailClick === 'function' && !this.busy;
        previous.disabled = !ready || index === 0;
        next.disabled = !ready || index >= count - 1;
      };
      const move = async direction => {
        if (!current() || this.busy || typeof owner.handleThumbnailClick !== 'function') return;
        const index = activeIndex() + direction;
        if (index < 0 || index >= items().length) return;
        this.busy = true; update();
        try { await owner.handleThumbnailClick(index); }
        catch { if (current()) counter.textContent = 'Повтори вибір фото'; }
        finally { this.busy = false; update(); }
      };
      previous.addEventListener('click', () => move(-1), { signal });
      next.addEventListener('click', () => move(1), { signal });
      const dialog = owner.querySelector('dialog');
      const track = owner.querySelector('.dialog-zoomed-gallery');
      dialog?.addEventListener('keydown', event => {
        if (event.defaultPrevented || !['ArrowLeft','ArrowRight'].includes(event.key) || event.target.closest?.('input,textarea,select')) return;
        event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      track?.addEventListener('scroll', () => owner.handleScroll?.(), { signal, passive:true });
      this.observer = new MutationObserver((records = []) => {
        update();
        if (!records.some(record => record.attributeName === 'open' && record.target.open)) return;
        cancelAnimationFrame(this.openFrame);
        this.openFrame = requestAnimationFrame(() => {
          this.openFrame = undefined;
          if (!current() || !dialog?.open || !track || typeof owner.selectThumbnail !== 'function') return;
          const frame = track.getBoundingClientRect();
          let best = 0, area = 0;
          items().forEach((item,index) => {
            const rect = item.getBoundingClientRect();
            const overlap = Math.max(0,Math.min(rect.right,frame.right)-Math.max(rect.left,frame.left)) * Math.max(0,Math.min(rect.bottom,frame.bottom)-Math.max(rect.top,frame.top));
            if (overlap > area) { area=overlap; best=index; }
          });
          if (area > 0) owner.selectThumbnail(best, {behavior:'instant'});
          update();
        });
      });
      this.observer.observe(owner, { attributes: true, subtree: true, attributeFilter: ['aria-selected', 'open'] });
      customElements.whenDefined('zoom-dialog').then(update);
      update();
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); cancelAnimationFrame(this.openFrame); }
  }
  if (!customElements.get('veyro-product-share')) customElements.define('veyro-product-share', VeyroProductShare);
  if (!customElements.get('veyro-zoom-controls')) customElements.define('veyro-zoom-controls', VeyroZoomControls);
})();
